import { FC, useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { NetworkAccountService } from "../../../shared/services/contentNetwork/networkAccounts/networkAccountService";

import NetworkResourceForm from "./NetworkResourceForm";
import showAlert from "../../../shared/utils/generic";

import "./styles.scss";
import { NetworkResource } from "@models/networkResource";
import socialMediaData from "src/shared/data/socialMediaData";

interface Props {
  show: boolean;
  onClose: () => void;
  resource: NetworkResource | null;
  onResourceUpdated: () => void;
}

const TYPE_CONFIG: Record<string, { label: string; icon: string; description: string }> = {
  account: {
    label: "Account",
    icon: "bi-person-circle",
    description: "Main advertising account",
  },
  page: {
    label: "Page",
    icon: "bi-file-earmark",
    description: "Facebook/Instagram page",
  },
  profile: {
    label: "Profile",
    icon: "bi-person-badge",
    description: "Snapchat profile",
  },
  board: {
    label: "Board",
    icon: "bi-grid-3x3",
    description: "Pinterest board",
  },
};

const NetworkResourceEditModal: FC<Props> = ({
  show,
  onClose,
  resource,
  onResourceUpdated,
}) => {
  const [formData, setFormData] = useState<Partial<NetworkResource>>({});
  const [loading, setLoading] = useState(false);

  const accountService = new NetworkAccountService();

  useEffect(() => {
    if (resource) {
      setFormData(resource);
    }
  }, [resource, show]);

  const handleClose = () => {
    setFormData({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!resource) return;

    // Validate required fields
    if (!formData.name || !formData.external_id) {
      showAlert("Please fill in all required fields", "error", 3000);
      return;
    }

    setLoading(true);
    try {
      await accountService.updateNetworkResource(resource.id, formData);
      
      showAlert("Resource updated successfully", "success", 3000);
      onResourceUpdated();
      handleClose();
    } catch (err) {
      console.error("Failed to update resource:", err);
      showAlert("Failed to update resource", "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!resource) return null;

  const networkKey = resource.network.toLowerCase();
  const typeKey = resource.type.toLowerCase();
  const networkData = socialMediaData[networkKey];
  const typeConfig = TYPE_CONFIG[typeKey] || {
    label: resource.type,
    icon: "bi-circle",
    description: "",
  };
  const showAuthKeys = typeKey === "account";

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Network Resource</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="d-flex align-items-center gap-2 mb-3 pb-3 border-bottom">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: "32px",
              height: "32px",
              backgroundColor: networkData?.color || "#6c757d",
              color: networkKey === "snapchat" ? "#000" : "#fff",
            }}
          >
            <i className={networkData?.icon || "bi-box"}></i>
          </div>
          <div>
            <h5 className="mb-0">
              {networkData?.label || resource.network} {typeConfig.label}
            </h5>
            <small className="text-muted">{typeConfig.description}</small>
          </div>
        </div>

        <NetworkResourceForm
          resource={formData}
          onChange={setFormData}
          showAuthKeys={showAuthKeys}
          showLinking={true}
          organizationId={resource.organization_id}
          resourceNetwork={resource.network}
          onLinksChanged={onResourceUpdated}
        />
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2"></i>
              Save Changes
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NetworkResourceEditModal;